import { GameState } from '../../core/GameState.js';
import { formatDuration } from '../../utils/formatting.js';
import { estimateRouteSchedule } from '../../utils/routeEta.js';

/**
 * Multi-stop route timing for the Fleet card chip and the Details per-stop
 * table (#53-T9e). Everything beyond the first arrival is an estimate, so
 * values carry a "~" prefix and the assumptions behind them sit in a tooltip.
 * The text builders are pure (no GameState/DOM) so they can be unit tested.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const FLAG_TEXT = {
  'smart-action': 'smart actions depend on prices at the time',
  conditional: 'conditional actions may or may not run',
  'stock-limited': 'port stock may limit how much is loaded',
  'warehouse-stock': 'depends on what the warehouse holds',
  detour: 'warehouse detour before the next stop'
};
export const FUEL_SHORT_TEXT = 'fuel may run short on this leg';

// Short reasons shown instead of times; reasons not listed show nothing.
const BLOCKED_TEXT = {
  'route-paused': 'Route paused - no loop estimate',
  'no-manager': 'No manager - no loop estimate'
};

const pad2 = n => String(n).padStart(2, '0');

export function shortDate(d) { return `${MONTHS[d.getMonth()]} ${pad2(d.getDate())}`; }
export function shortDateTime(d) { return `${shortDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`; }

/** "under 1 hr" below an hour, else "2 days 4 hrs". */
export function hoursText(hours) {
  return hours < 1 ? 'under 1 hr' : formatDuration(hours / 24);
}

/** Tooltip text for a stop's flags (+ fuel warning); '' when none. */
export function flagsTitle(stop) {
  const parts = (stop.flags || []).map(f => FLAG_TEXT[f]).filter(Boolean);
  if (stop.fuelShort) parts.push(FUEL_SHORT_TEXT);
  return parts.length ? `Estimate - ${parts.join('; ')}` : '';
}

/** Short reason for a blocked schedule, or '' to show nothing. */
export function blockedText(reason) { return BLOCKED_TEXT[reason] || ''; }

/**
 * Chip wording. loop is never shown for blocked / opportunistic schedules.
 * @returns {{text:string, title:string, blocked:boolean}|null}
 */
export function describeLoopChip(schedule, portName) {
  if (!schedule) return null;
  if (schedule.blockedReason) {
    const text = blockedText(schedule.blockedReason);
    return text ? { text, title: '', blocked: true } : null;
  }
  if (!schedule.loop) return null;
  const home = schedule.stops.find(s => s.stopIndex >= 0);
  const back = home ? ` - back at ${portName(home.portId)} ~${shortDate(schedule.loop.endAt)}` : '';
  const flagged = schedule.stops.some(s => (s.flags || []).length || s.fuelShort);
  const title = 'Estimate - port work, prices and detours can change it'
    + (flagged ? '. Per-stop assumptions are in Details.' : '');
  return { text: `Loop ~${hoursText(schedule.loop.hours)}${back}`, title, blocked: false };
}

/**
 * Per-stop rows. Row 0 of a live schedule is exact for its arrival (no "~"),
 * everything after it is estimated.
 * @returns {{name:string, arrive:string, port:string, title:string}[]}
 */
export function describeStopRows(schedule, portName, now) {
  if (!schedule || schedule.blockedReason || !schedule.loop) return [];
  return schedule.stops.map((s, i) => {
    const here = i === 0 && s.arriveAt <= now;
    const arrive = here ? 'in port now' : `arrive ${i === 0 ? '' : '~'}${shortDateTime(s.arriveAt)}`;
    let port = '';
    if (s.portHoursSource === 'live') port = `${hoursText(s.portHours)} left in port`;
    else if (s.portHoursSource === 'estimate') port = `~${hoursText(s.portHours)} in port`;
    return { name: portName(s.portId), arrive, port, title: flagsTitle(s) };
  });
}

// ---------- GameState glue ----------
function _portName(id) { return GameState.getPort(id)?.name || id || '?'; }

/** Live schedule for a ship on an automated route; null when it has none. */
function _schedule(ship) {
  const route = ship?.assignedRouteId ? GameState.tradeRoutes?.get(ship.assignedRouteId) : null;
  if (!route) return null;
  return estimateRouteSchedule(route, ship, {
    mode: 'live',
    now: GameState.currentDate,
    getPort: id => GameState.getPort(id),
    manager: GameState.routeManagers?.get(route.managerId) || null,
    warehouses: GameState.warehouses,
    portStock: id => GameState.portStock?.get(id)
  });
}

/** Fleet card chip; '' when the ship has no route or nothing sensible to say. */
export function routeLoopChipHtml(ship) {
  const d = describeLoopChip(_schedule(ship), _portName);
  if (!d) return '';
  return `<span class="ship-loop${d.blocked ? ' ship-loop--none' : ''}"${d.title ? ` title="${d.title}"` : ''}>${d.text}</span>`;
}

/** Details-view heading + per-stop table; '' when there is nothing to list. */
export function routeStopsTableHtml(ship) {
  const rows = describeStopRows(_schedule(ship), _portName, GameState.currentDate);
  if (!rows.length) return '';
  const body = rows.map(r => `<tr${r.title ? ` title="${r.title}"` : ''}>`
    + `<td>${r.name}${r.title ? ' *' : ''}</td><td>${r.arrive}${r.port ? ` - ${r.port}` : ''}</td></tr>`).join('');
  return `<h6>Route Schedule <small>(estimate)</small></h6><table class="stats-table route-eta-table">${body}</table>`;
}
